import { Component, inject, effect, HostListener, ElementRef, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AuthStateService } from '@dental-ui/auth';
import { buildApiUrl, buildRequestHeaders } from '@dental-ui/shared/consts';
import { SignalRService } from '@dental-ui/data-access';
import { Subscription, Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { TranslateService, TranslatePipe } from '@dental-ui/shared/i18n';
import { NotificationDto, NotificationItem, NotificationSubTypeEnum, NotificationTypeEnum } from './notifications.model';
import { NotificationFilterRequest } from './notification-filter.model';
import { LucideBell, LucideFunnel, LucideX } from '@lucide/angular';

export interface NotificationFilterItem {
  label: string;
  value: NotificationSubTypeEnum | null;
}

@Component({
  selector: 'lib-notifications',
  standalone: true,
  imports: [CommonModule, TranslatePipe, LucideBell, LucideFunnel, LucideX],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss'
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private readonly authStateService = inject(AuthStateService);
  private readonly http = inject(HttpClient);
  private readonly elementRef = inject(ElementRef);
  private readonly signalRService = inject(SignalRService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  private signalRSubscription?: Subscription;
  private notificationRequestId = 0;
  private readonly branchTypeToken = /system\.int32\[\]/i;
  private readonly notificationSources = new Map<string, string>();
  private branchTypesLoaded = false;
  private branchTypesLoading = false;
  private readonly branchTypeById = new Map<string, number[]>();
  private readonly branchTypeByName = new Map<string, number[]>();

  @Input() iconColor: 'dark' | 'light' = 'dark';
  @Input() appType: 'account' | 'clinic' | 'lab' = 'clinic';
  @Output() notificationsOpenChange = new EventEmitter<boolean>();

  isNotificationsOpen = false;
  isNotificationsExpanded = false;
  isNotificationDrawerOpen = false;
  isAccountFilterMenuOpen = false;
  accountReadFilter: 'all' | 'unread' = 'all';
  accountScopeFilter: 'all' | 'subscription' | 'team' | 'system' | number = 'all';
  unreadNotificationCount = 0;
  notifications: NotificationItem[] = [];
  
  notificationFilters: NotificationFilterItem[] = [];
  activeNotificationFilter: NotificationSubTypeEnum | null = null;

  get allNotificationFilter(): NotificationFilterItem | undefined {
    return this.notificationFilters.find((filter) => filter.value === null);
  }

  get secondaryNotificationFilters(): NotificationFilterItem[] {
    return this.notificationFilters.filter((filter) => filter.value !== null);
  }

  constructor() {
    effect(() => {
      const user = this.authStateService.user();
      if (user?.userId) {
        this.fetchUnreadNotificationCount();
      }
    });
  }

  ngOnInit(): void {
    this.setupFilters();
    this.signalRSubscription = this.signalRService.getAllNotificationAsyncReceived$.subscribe(() => {
      // Refresh count when a new real-time notification is received
      this.fetchUnreadNotificationCount();
      // If the dropdown is open, also refresh the list
      if (this.isNotificationsOpen || this.isNotificationDrawerOpen) {
        this.fetchNotifications(this.usesDentcamNotify() ? 500 : 50, this.usesDentcamNotify());
      }
    });
  }

  setupFilters(): void {
    const allFilter: NotificationFilterItem = { label: 'NOTIFICATIONS.FILTERS.ALL', value: null };
    
    if (this.appType === 'account') {
      this.notificationFilters = [
        allFilter,
        { label: 'NOTIFICATIONS.FILTERS.SUBSCRIPTIONS', value: NotificationSubTypeEnum.Subscription },
        { label: 'NOTIFICATIONS.FILTERS.ORGANIZATION', value: NotificationSubTypeEnum.Organization },
        { label: 'NOTIFICATIONS.FILTERS.USERS', value: NotificationSubTypeEnum.Users },
        { label: 'NOTIFICATIONS.FILTERS.SERVICE_LIST', value: NotificationSubTypeEnum.ServiceList },
        { label: 'NOTIFICATIONS.FILTERS.PRICE_LISTS', value: NotificationSubTypeEnum.PriceLists },
        { label: 'NOTIFICATIONS.FILTERS.PARTNERS', value: NotificationSubTypeEnum.Partners },
      ];
    } else if (this.appType === 'lab') {
      this.notificationFilters = [
        allFilter,
        { label: 'NOTIFICATIONS.FILTERS.DESIGN_PROJECTS', value: NotificationSubTypeEnum.DesignProject },
        { label: 'NOTIFICATIONS.FILTERS.SYSTEM', value: NotificationSubTypeEnum.System },
      ];
    } else {
      this.notificationFilters = [
        allFilter,
        { label: 'NOTIFICATIONS.FILTERS.PANEL', value: NotificationSubTypeEnum.Panel },
        { label: 'NOTIFICATIONS.FILTERS.APPOINTMENTS', value: NotificationSubTypeEnum.Appointment },
        { label: 'NOTIFICATIONS.FILTERS.TREATMENTS', value: NotificationSubTypeEnum.Treatment },
        { label: 'NOTIFICATIONS.FILTERS.WORK_ORDERS', value: NotificationSubTypeEnum.Work_Order },
        { label: 'NOTIFICATIONS.FILTERS.PAYMENTS', value: NotificationSubTypeEnum.Payment },
        { label: 'NOTIFICATIONS.FILTERS.SYSTEM', value: NotificationSubTypeEnum.System },
      ];
    }
  }

  ngOnDestroy(): void {
    if (this.signalRSubscription) {
      this.signalRSubscription.unsubscribe();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as Node | null;
    const filterWrap = this.elementRef.nativeElement.querySelector('.notify-filter-wrap');
    if (!(filterWrap && target && filterWrap.contains(target))) {
      this.isAccountFilterMenuOpen = false;
    }
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.close();
    }
  }

  usesDentcamNotify(): boolean {
    return this.appType === 'account' || this.appType === 'lab';
  }

  close(): void {
    this.isAccountFilterMenuOpen = false;
    if (this.isNotificationsOpen) {
      this.isNotificationsOpen = false;
      this.notificationsOpenChange.emit(false);
    }
  }

  closeDrawer(): void {
    this.isNotificationDrawerOpen = false;
    this.isAccountFilterMenuOpen = false;
  }

  toggleNotifications(): void {
    if (this.isNotificationDrawerOpen) {
      this.closeDrawer();
      return;
    }
    this.isNotificationsOpen = !this.isNotificationsOpen;
    this.notificationsOpenChange.emit(this.isNotificationsOpen);
    this.isAccountFilterMenuOpen = false;
    if (this.isNotificationsOpen) {
      this.isNotificationsExpanded = false;
      this.fetchNotifications(this.usesDentcamNotify() ? 500 : 50, this.usesDentcamNotify());
    }
  }

  openAllNotifications(): void {
    this.isNotificationsOpen = false;
    this.isAccountFilterMenuOpen = false;
    this.notificationsOpenChange.emit(false);
    this.isNotificationDrawerOpen = true;
    if (!this.notifications.length) {
      this.fetchNotifications(500, true);
    }
  }

  setAccountReadFilter(filter: 'all' | 'unread'): void {
    this.accountReadFilter = filter;
  }

  setAccountScopeFilter(filter: 'all' | 'subscription' | 'team' | 'system' | number | null): void {
    this.accountScopeFilter = filter === null ? 'all' : filter;
    this.isAccountFilterMenuOpen = false;
  }

  isLabScopeActive(filter: NotificationFilterItem): boolean {
    if (filter.value === null) {
      return this.accountScopeFilter === 'all';
    }
    return this.accountScopeFilter === filter.value;
  }

  toggleAccountFilterMenu(event: Event): void {
    event.stopPropagation();
    this.isAccountFilterMenuOpen = !this.isAccountFilterMenuOpen;
  }

  get accountTeamSubTypes(): number[] {
    return [NotificationSubTypeEnum.Users, NotificationSubTypeEnum.Partners];
  }

  get accountNotifications(): NotificationItem[] {
    const allowed = this.currentValidSubTypes;
    const team = new Set(this.accountTeamSubTypes);
    return this.notifications.filter((item) => {
      const subType = Number(item.subType);
      if (!allowed.includes(subType)) {
        return false;
      }
      if (this.accountReadFilter === 'unread' && !item.unread) {
        return false;
      }
      if (this.accountScopeFilter === 'subscription') {
        return subType === NotificationSubTypeEnum.Subscription;
      }
      if (this.accountScopeFilter === 'team') {
        return team.has(subType);
      }
      if (this.accountScopeFilter === 'system') {
        return !team.has(subType) && subType !== NotificationSubTypeEnum.Subscription;
      }
      if (typeof this.accountScopeFilter === 'number') {
        return subType === this.accountScopeFilter;
      }
      return true;
    });
  }

  get accountPreviewNotifications(): NotificationItem[] {
    return this.accountNotifications.slice(0, 5);
  }

  get accountTodayNotifications(): NotificationItem[] {
    return this.accountNotifications.filter((item) => this.isToday(item.date));
  }

  get accountEarlierNotifications(): NotificationItem[] {
    return this.accountNotifications.filter(
      (item) => !this.isToday(item.date) && this.isWithinPastWeek(item.date),
    );
  }

  accountIconKind(item: NotificationItem): 'users' | 'info' | 'clock' {
    const subType = Number(item.subType);
    if (this.accountTeamSubTypes.includes(subType)) {
      return 'users';
    }
    if (subType === NotificationSubTypeEnum.Subscription || subType === NotificationSubTypeEnum.Payment) {
      return 'info';
    }
    return 'clock';
  }

  toggleNotificationsExpanded(): void {
    this.isNotificationsExpanded = !this.isNotificationsExpanded;
  }

  setNotificationFilter(filter: NotificationFilterItem) {
    this.activeNotificationFilter = filter.value;
    this.fetchNotifications(50);
  }

  get validAccountSubTypes(): number[] {
    return [
      NotificationSubTypeEnum.Subscription,
      NotificationSubTypeEnum.Organization,
      NotificationSubTypeEnum.Users,
      NotificationSubTypeEnum.ServiceList,
      NotificationSubTypeEnum.PriceLists,
      NotificationSubTypeEnum.Partners
    ];
  }

  get validClinicSubTypes(): number[] {
    return [
      NotificationSubTypeEnum.Panel,
      NotificationSubTypeEnum.Appointment,
      NotificationSubTypeEnum.Treatment,
      NotificationSubTypeEnum.Work_Order,
      NotificationSubTypeEnum.Payment,
      NotificationSubTypeEnum.System
    ];
  }

  get validLabSubTypes(): number[] {
    return [
      NotificationSubTypeEnum.DesignProject,
      NotificationSubTypeEnum.System
    ];
  }

  get filteredNotifications(): NotificationItem[] {
    let subTypes: number[] = [];
    if (this.appType === 'account') {
      subTypes = this.validAccountSubTypes;
    } else if (this.appType === 'clinic') {
      subTypes = this.validClinicSubTypes;
    } else if (this.appType === 'lab') {
      subTypes = this.validLabSubTypes;
    }

    return this.notifications.filter(item => {
      const subTypeVal = Number(item.subType);
      if (this.activeNotificationFilter !== null) {
        return subTypeVal === this.activeNotificationFilter;
      }
      return subTypes.includes(subTypeVal);
    });
  }

  get displayedNotifications(): NotificationItem[] {
    return this.isNotificationsExpanded
      ? this.filteredNotifications
      : this.filteredNotifications.slice(0, 5);
  }

  get notificationBadgeText(): string {
    const limit = this.appType === 'clinic' ? 99 : 9;
    return this.unreadNotificationCount > limit ? `${limit}+` : String(this.unreadNotificationCount);
  }

  fetchUnreadNotificationCount(): void {
    const userId = this.authStateService.user()?.userId;
    if (!userId) return;

    let subType: number[] = [];
    if (this.appType === 'account') {
      subType = this.validAccountSubTypes;
    } else if (this.appType === 'clinic') {
      subType = this.validClinicSubTypes;
    } else if (this.appType === 'lab') {
      subType = this.validLabSubTypes;
    }

    const url = buildApiUrl('notification.getCountByUser');
    const body = {
      user: userId,
      subType
    };

    this.http.post<{success?: boolean; data?: number}>(url, body, { headers: buildRequestHeaders(true) }).subscribe({
      next: (res) => {
        if (res.success && res.data !== undefined) {
          this.unreadNotificationCount = res.data;
        }
      },
      error: (err) => console.error('Failed to fetch unread notification count', err)
    });
  }

  fetchNotifications(numberOfRecord = 50, allSubTypes = false): void {
    const userId = this.authStateService.user()?.userId;
    if (!userId) return;

    const requestId = ++this.notificationRequestId;
    const subTypes =
      allSubTypes || this.activeNotificationFilter === null
        ? this.currentValidSubTypes
        : [this.activeNotificationFilter];

    if (!subTypes.length) {
      this.notifications = [];
      return;
    }

    forkJoin(
      subTypes.map((subType) =>
        this.requestNotifications(userId, subType, numberOfRecord).pipe(
          catchError((err) => {
            console.error('Failed to fetch notifications', err);
            return of([] as NotificationDto[]);
          }),
        ),
      ),
    ).subscribe((pages) => {
      if (requestId !== this.notificationRequestId) return;
      this.notifications = this.mapNotifications(this.mergeNotificationDtos(pages.flat()));
      this.ensureBranchTypeLabels();
    });
  }

  private get currentValidSubTypes(): number[] {
    if (this.appType === 'account') return this.validAccountSubTypes;
    if (this.appType === 'clinic') return this.validClinicSubTypes;
    if (this.appType === 'lab') return this.validLabSubTypes;
    return [];
  }

  private requestNotifications(
    userId: string,
    subType: number,
    numberOfRecord: number,
  ): Observable<NotificationDto[]> {
    const request = new NotificationFilterRequest();
    request.paginationRequestModel.setData({
      page: 0,
      numberOfRecord,
      sortColumn: 'IsRead',
      sortOrder: 'asc',
    });
    request.notificationDto.setData({
      sharedUserId: userId,
      subType,
    });

    const url = buildApiUrl('notification.getAllByFilter');

    return this.http
      .post<{ success?: boolean; data?: { data?: NotificationDto[] } | NotificationDto[] }>(
        url,
        {
          PaginationRequestModel: {
            Page: request.paginationRequestModel.page + 1,
            NumberOfRecord: request.paginationRequestModel.numberOfRecord,
            SortColumn: request.paginationRequestModel.sortColumn,
            SortOrder: request.paginationRequestModel.sortOrder,
          },
          NotificationDto: {
            sharedUserId: request.notificationDto.sharedUserId,
            type: request.notificationDto.type,
            subType: request.notificationDto.subType,
            title: request.notificationDto.title,
            description: request.notificationDto.description,
          },
        },
        { headers: buildRequestHeaders(true) },
      )
      .pipe(
        map((res) => {
          const payload = res.data;
          const items = Array.isArray(payload)
            ? payload
            : (payload?.data ?? []);
          return res.success ? items : [];
        }),
      );
  }

  private mergeNotificationDtos(items: NotificationDto[]): NotificationDto[] {
    const byId = new Map<string, NotificationDto>();
    for (const item of items) {
      if (!byId.has(item.id)) {
        byId.set(item.id, item);
      }
    }
    return [...byId.values()].sort((a, b) => {
      if (a.isRead !== b.isRead) return a.isRead ? 1 : -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }

  private ensureBranchTypeLabels(): void {
    const needsLookup = this.notifications.some((item) =>
      this.branchTypeToken.test(this.notificationSources.get(item.id) ?? item.description),
    );
    if (!needsLookup) return;

    if (this.branchTypesLoaded) {
      this.notifications = this.notifications.map((item) => ({
        ...item,
        description: this.withBranchTypeLabel(
          this.notificationSources.get(item.id) ?? item.description,
          item.parameters,
          item.entityId,
        ),
      }));
      return;
    }

    if (this.branchTypesLoading) return;
    this.branchTypesLoading = true;

    const url = buildApiUrl('branch.getAllByOrganizationWithFilter');
    this.http
      .post<{ success?: boolean; data?: { data?: Array<{ id?: string; description?: string | null; type?: number[] | null }> } }>(
        url,
        {
          PaginationRequestModel: {
            Page: 1,
            NumberOfRecord: 500,
            SortColumn: 'description',
            SortOrder: 'asc',
          },
          BranchFilterRequestDto: {
            description: '',
            organizationDescription: '',
            userFirstName: '',
            userLastName: '',
          },
        },
        { headers: buildRequestHeaders(true) },
      )
      .subscribe({
        next: (res) => {
          this.branchTypesLoading = false;
          this.branchTypesLoaded = true;
          for (const branch of res.success ? (res.data?.data ?? []) : []) {
            const types = (branch.type ?? []).filter((type) => type === 1 || type === 2);
            if (!types.length) continue;
            if (branch.id) this.branchTypeById.set(branch.id, types);
            const name = branch.description?.trim().toLocaleLowerCase();
            if (name) this.branchTypeByName.set(name, types);
          }
          this.notifications = this.notifications.map((item) => ({
            ...item,
            description: this.withBranchTypeLabel(
              this.notificationSources.get(item.id) ?? item.description,
              item.parameters,
              item.entityId,
            ),
          }));
        },
        error: () => {
          this.branchTypesLoading = false;
        },
      });
  }

  private withBranchTypeLabel(description: string, parameters: unknown, entityId: string): string {
    if (!description || !this.branchTypeToken.test(description)) return description ?? '';
    const label = this.branchTypeLabel(this.resolveBranchTypes(description, parameters, entityId));
    if (!label) return this.omitUnknownBranchType(description);
    return description.replace(/system\.int32\[\]/gi, label);
  }

  private omitUnknownBranchType(description: string): string {
    let text = description.replace(/\s+-\s+system\.int32\[\]\s+-\s+/gi, ' - ');
    text = text.replace(/\s+-\s+system\.int32\[\]/gi, '');
    text = text.replace(/system\.int32\[\]\s+-\s+/gi, '');
    text = text.replace(/system\.int32\[\]/gi, '');
    text = text.replace(/\s+-\s+,/g, '');
    text = text.replace(/,\s*$/g, '');
    text = text.replace(/\s+-\s*$/g, '');
    text = text.replace(/^\s*-\s+/g, '');
    return text.replace(/\s{2,}/g, ' ').trim();
  }

  private resolveBranchTypes(description: string, parameters: unknown, entityId: string): number[] {
    const fromParameters = this.extractBranchTypes(parameters);
    if (fromParameters.length) return fromParameters;

    const fromId = entityId ? this.branchTypeById.get(entityId) : undefined;
    if (fromId?.length) return fromId;

    const name = description.split(/ - system\.int32\[\]/i)[0]?.trim().toLocaleLowerCase();
    return (name && this.branchTypeByName.get(name)) || [];
  }

  private extractBranchTypes(value: unknown): number[] {
    if (Array.isArray(value)) {
      const direct = value
        .map((entry) => (typeof entry === 'string' ? entry.trim() : entry))
        .filter((entry) => entry === 1 || entry === 2 || entry === '1' || entry === '2')
        .map((entry) => Number(entry));
      if (direct.length === value.length && direct.length > 0) {
        return [...new Set(direct)].sort((a, b) => a - b);
      }
      for (const entry of value) {
        const nested = this.extractBranchTypes(entry);
        if (nested.length) return nested;
      }
      return [];
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return [];
      try {
        return this.extractBranchTypes(JSON.parse(trimmed));
      } catch {
        return [];
      }
    }

    return [];
  }

  private branchTypeLabel(types: number[]): string {
    const labels = [...new Set(types)]
      .sort((a, b) => a - b)
      .map((type) => this.translatedBranchType(type))
      .filter((label): label is string => !!label);
    return labels.join(', ');
  }

  private translatedBranchType(type: number): string | null {
    const key = type === 1 ? 'NOTIFICATIONS.BRANCH_CLINIC' : type === 2 ? 'NOTIFICATIONS.BRANCH_LAB' : '';
    if (!key) return null;
    const translated = this.translateService.getText(key);
    if (translated !== key) return translated;
    const turkish = this.translateService.getCurrentLanguage().toLowerCase().startsWith('tr');
    if (type === 1) return turkish ? 'Klinik' : 'Clinic';
    return 'Lab';
  }

  private mapNotifications(items: NotificationDto[]): NotificationItem[] {
    return items.map((item) => {
      const icon = this.getNotificationIcon(item.subType as NotificationSubTypeEnum);
      const source = item.description ?? '';
      this.notificationSources.set(item.id, source);
      return {
        id: item.id,
        sharingUserId: item.sharingUserId,
        sharedUserId: item.sharedUserId,
        isAdmin: item.isAdmin,
        isSystem: item.isSystem,
        isAllUser: item.isAllUser,
        entityId: item.entityId,
        entity2Id: item.entity2Id,
        title: item.title,
        description: this.withBranchTypeLabel(source, item.parameters, item.entityId),
        parameters: item.parameters,
        time: this.usesDentcamNotify() ? this.formatAccountTime(item.date) : this.formatTimeAgo(item.date),
        date: item.date,
        type: item.type,
        subType: item.subType,
        icon: icon,
        unread: !item.isRead,
        modifiedBy: item.modifiedBy,
      };
    });
  }

  getNotificationIcon(subType: NotificationSubTypeEnum): string {
    switch(subType) {
      case NotificationSubTypeEnum.Appointment: return 'calendar_today';
      case NotificationSubTypeEnum.Payment: return 'credit_card';
      case NotificationSubTypeEnum.DesignProject: return 'design_services';
      case NotificationSubTypeEnum.Work_Order: return 'build';
      case NotificationSubTypeEnum.Treatment: return 'medical_services';
      case NotificationSubTypeEnum.Subscription: return 'card_membership';
      case NotificationSubTypeEnum.Organization: return 'business';
      case NotificationSubTypeEnum.Users: return 'group';
      case NotificationSubTypeEnum.Partners: return 'handshake';
      default: return 'notifications';
    }
  }

  onNotificationClick(notif: NotificationItem): void {
    // 1. Mark as read
    if (notif.unread) {
      this.markAsRead(notif);
    }

    this.close();
    this.closeDrawer();

    // 3. Navigate
    this.navigateToNotification(notif);
  }

  navigateToNotification(notif: NotificationItem): void {
    const type = notif.type;
    if (type === undefined) return;

    if (this.appType === 'account') {
      switch (type) {
        case NotificationTypeEnum.Subscription_Started:
        case NotificationTypeEnum.Subscription_Cancelled:
        case NotificationTypeEnum.Subscription_Renewing_Soon:
        case NotificationTypeEnum.Trial_Period_Ending_Soon:
        case NotificationTypeEnum.Trial_Period_Ended:
          this.router.navigate(['/account/subscriptions']);
          break;
        case NotificationTypeEnum.Organization_Updated:
        case NotificationTypeEnum.Branch_Added:
        case NotificationTypeEnum.Branch_Updated:
        case NotificationTypeEnum.Branch_Removed:
          this.router.navigate(['/account/organization']);
          break;
        case NotificationTypeEnum.User_Invited:
        case NotificationTypeEnum.User_Invitation_Accepted:
        case NotificationTypeEnum.User_Removed:
        case NotificationTypeEnum.User_Information_Updated:
          this.router.navigate(['/account/team']);
          break;
        case NotificationTypeEnum.Service_Added:
        case NotificationTypeEnum.Service_Updated:
        case NotificationTypeEnum.Service_Removed:
        case NotificationTypeEnum.Service_Activated:
        case NotificationTypeEnum.Service_Deactivated:
          this.router.navigate(['/account/services'], { queryParams: { tab: 'services' } });
          break;
        case NotificationTypeEnum.Service_Group_Added:
        case NotificationTypeEnum.Service_Group_Updated:
        case NotificationTypeEnum.Service_Group_Removed:
          this.router.navigate(['/account/services'], { queryParams: { tab: 'groups' } });
          break;
        case NotificationTypeEnum.Doctor_Assigned:
        case NotificationTypeEnum.Doctor_Assignment_Removed:
          this.router.navigate(['/account/services'], { queryParams: { tab: 'doctors' } });
          break;
        case NotificationTypeEnum.Price_List_Created:
        case NotificationTypeEnum.Price_List_Updated:
        case NotificationTypeEnum.Price_List_Removed:
        case NotificationTypeEnum.Price_List_Activated:
        case NotificationTypeEnum.Price_List_Deactivated:
          this.router.navigate(['/account/prices']);
          break;
        case NotificationTypeEnum.Partner_Added:
        case NotificationTypeEnum.Partner_Removed:
        case NotificationTypeEnum.Partner_Invitation_Accepted:
        case NotificationTypeEnum.Partner_Invitation_Rejected:
          this.router.navigate(['/account/partners']);
          break;
      }
    } else if (this.appType === 'lab') {
      switch (type) {
        case NotificationTypeEnum.Dashboard_Updated:
          this.router.navigate(['/main']);
          break;
        case NotificationTypeEnum.New_Work_Order:
        case NotificationTypeEnum.Work_Order_Created:
        case NotificationTypeEnum.Work_Order_Updated:
        case NotificationTypeEnum.Work_Order_Approved:
        case NotificationTypeEnum.Work_Order_In_Design:
        case NotificationTypeEnum.Work_Order_In_Production:
        case NotificationTypeEnum.Work_Order_Shipped:
        case NotificationTypeEnum.Work_Order_Completed:
        case NotificationTypeEnum.Work_Order_Due_Date_Approaching:
        case NotificationTypeEnum.Work_Order_Due_Date_Delayed:
          if (notif.entityId) {
            this.router.navigate(['/workorders', notif.entityId]);
          } else {
            this.router.navigate(['/workorders']);
          }
          break;
        case NotificationTypeEnum.Payment_Created:
        case NotificationTypeEnum.Payment_Updated:
        case NotificationTypeEnum.Payment_Received:
        case NotificationTypeEnum.Payment_Completed:
        case NotificationTypeEnum.Payment_Cancelled:
        case NotificationTypeEnum.Payment_Refunded:
        case NotificationTypeEnum.Payment_Due_Soon:
        case NotificationTypeEnum.Payment_Delayed:
          this.router.navigate(['/payments']);
          break;
        case NotificationTypeEnum.Design_Project_Created:
        case NotificationTypeEnum.Design_Project_Removed:
          this.router.navigate(['/design-projects']);
          break;
      }
    } else if (this.appType === 'clinic') {
      switch (type) {
        case NotificationTypeEnum.Dashboard_Updated:
          this.router.navigate(['/main']);
          break;
        case NotificationTypeEnum.Appointment_Created:
        case NotificationTypeEnum.Appointment_Updated:
        case NotificationTypeEnum.Upcoming_Appointment:
        case NotificationTypeEnum.Appointment_Completed:
        case NotificationTypeEnum.Appointment_Cancelled:
          this.router.navigate(['/appointment'], { queryParams: { id: notif.entityId } });
          break;
        case NotificationTypeEnum.Treatment_Created:
        case NotificationTypeEnum.Treatment_Updated:
        case NotificationTypeEnum.Treatment_Completed:
        case NotificationTypeEnum.Treatment_Cancelled:
          this.router.navigate(['/treatments'], { queryParams: { id: notif.entityId } });
          break;
        case NotificationTypeEnum.New_Work_Order:
        case NotificationTypeEnum.Work_Order_Created:
        case NotificationTypeEnum.Work_Order_Updated:
        case NotificationTypeEnum.Work_Order_Approved:
        case NotificationTypeEnum.Work_Order_In_Design:
        case NotificationTypeEnum.Work_Order_In_Production:
        case NotificationTypeEnum.Work_Order_Shipped:
        case NotificationTypeEnum.Work_Order_Completed:
        case NotificationTypeEnum.Work_Order_Due_Date_Approaching:
        case NotificationTypeEnum.Work_Order_Due_Date_Delayed:
          if (notif.entityId) {
            this.router.navigate(['/workorder-detail', notif.entityId]);
          } else {
            this.router.navigate(['/workorders']);
          }
          break;
        case NotificationTypeEnum.Payment_Created:
        case NotificationTypeEnum.Payment_Updated:
        case NotificationTypeEnum.Payment_Received:
        case NotificationTypeEnum.Payment_Completed:
        case NotificationTypeEnum.Payment_Cancelled:
        case NotificationTypeEnum.Payment_Refunded:
        case NotificationTypeEnum.Payment_Due_Soon:
        case NotificationTypeEnum.Payment_Delayed:
          this.router.navigate(['/payments'], { queryParams: { id: notif.entityId } });
          break;
      }
    }
  }

  markAsRead(notif: NotificationItem): void {
    if (!notif.unread) return;

    const url = buildApiUrl('notification.read');
    const payload = {
      id: notif.id,
      sharedUserId: notif.sharedUserId
    };

    this.http.put<unknown>(url, payload, { headers: buildRequestHeaders(true) }).subscribe({
      next: () => {
        notif.unread = false;
        this.fetchUnreadNotificationCount();
      },
      error: (err) => console.error('Failed to mark notification as read', err)
    });
  }

  markAllAsRead(): void {
    const userId = this.authStateService.user()?.userId;
    if (!userId) return;

    const url = buildApiUrl('notification.readAll');
    const payload = {
      sharedUserId: userId
    };

    this.http.put<unknown>(url, payload, { headers: buildRequestHeaders(true) }).subscribe({
      next: () => {
        this.notifications.forEach(n => n.unread = false);
        this.fetchUnreadNotificationCount();
      },
      error: (err) => console.error('Failed to mark all notifications as read', err)
    });
  }

  private isToday(dateString: string): boolean {
    const date = new Date(dateString);
    const now = new Date();
    return date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
  }

  private isWithinPastWeek(dateString: string): boolean {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDiff = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86400000);
    return dayDiff > 0 && dayDiff < 7;
  }

  private formatAccountTime(dateString: string): string {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDiff = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86400000);
    const diffHours = Math.floor((now.getTime() - date.getTime()) / 3600000);
    const locale = this.translateService.getCurrentLanguage() === 'tr' ? 'tr-TR' : 'en-GB';

    if (dayDiff <= 0) {
      if (diffHours < 1) {
        const minutes = Math.max(1, Math.floor((now.getTime() - date.getTime()) / 60000));
        return this.translateService.getText('NOTIFICATIONS.TIME.MINUTES_SHORT').replace('$1', String(minutes));
      }
      return this.translateService.getText('NOTIFICATIONS.TIME.HOURS_SHORT').replace('$1', String(Math.max(1, diffHours)));
    }
    if (dayDiff === 1) {
      return this.translateService.getText('NOTIFICATIONS.TIME.YESTERDAY');
    }
    if (dayDiff < 7) {
      return date.toLocaleDateString(locale, { weekday: 'short' });
    }
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  }

  formatTimeAgo(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return this.translateService.getText('NOTIFICATIONS.TIME.MINUTES_AGO').replace('$1', Math.max(0, diffMins).toString());
    } else if (diffHours < 24) {
      return this.translateService.getText('NOTIFICATIONS.TIME.HOURS_AGO').replace('$1', diffHours.toString());
    } else {
      return this.translateService.getText('NOTIFICATIONS.TIME.DAYS_AGO').replace('$1', diffDays.toString());
    }
  }
}
